import { Component, Vue, Prop } from 'vue-property-decorator';
import { ToasterService } from '@/Services/ToasterService';
import { Contract, ProcessConfigPersonnelType } from '@/Domain/Entities';
import i18n from '@/lang/index';
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import { Toggle, MdSelect } from '@/Components';
import ReplacementContractsService from '@/Services/ReplacementContractsService';

@Component({
    components: { Toggle, MdSelect },
    computed: {
        ...mapState('dateFormatStore', { dateFormat: 'dateFormat'}),
        ...mapState('languagesStore', { currentLanguage: 'current' }),
        // Catálogo completo (incluidos los ocultos), cargado por App.vue y refrescado desde Configuración de procesos.
        ...mapGetters('processConfigStore', { processConfigPersonnelTypes: 'getProcessConfigPersonnelTypes' }),
        personnelTypeOptions() {
            // Solo se ofrecen los activos que aplican a contratos, salvo el tipo actual del contrato aunque esté
            // oculto o ya no aplique a contratos (el catálogo completo lo resuelve).
            const personnelTypes: ProcessConfigPersonnelType[] = (this as ReplacementContract).processConfigPersonnelTypes || [];
            const currentPersonnelTypeId = (this as ReplacementContract).contract ? (this as ReplacementContract).contract!.personnelTypeId : undefined;
            const selectable = personnelTypes.filter(personnelType =>
                (personnelType.active && personnelType.appliesToContracts) || personnelType.id === currentPersonnelTypeId);
            return _.orderBy(selectable.map(personnelType => ({
                id: personnelType.id,
                name: (this as ReplacementContract).personnelTypeName(personnelType.id)
            })), [option => _.deburr(option.name).toLowerCase()]);
        }
    }
})
export default class ReplacementContract extends Vue {
    @Prop() replacementListCandidateId!: string;
    @Prop() replacementListModified!: string;
    dateFormat!: string;
    currentLanguage!: string;
    processConfigPersonnelTypes!: ProcessConfigPersonnelType[];

    moment = moment;
    replacementContracts: any[] = [];
    contract: Contract | null = null;
    loading: boolean = false;
    showDeleteModal = false;
    openContract: boolean = false;
    submitted: boolean = false;
    fistContractRegister: boolean = false;

    $refs!: {
        replacementContractForm: HTMLFormElement
    };

    // El título se resuelve contra el catálogo completo: un tipo oculto sigue mostrándose en las tarjetas.
    personnelTypeName(personnelTypeId: string): string {
        const personnelType = (this.processConfigPersonnelTypes || []).find(type => type.id === personnelTypeId);
        if (!personnelType) {
            return '';
        }
        // Sin traducción en el idioma actual: es-ES y, si tampoco, el primer idioma con valor (como el CSV).
        const title = personnelType.title;
        return title[this.currentLanguage] || title['es-ES']
            || (Object.values(title).find(value => typeof value === 'string' && value !== '') as string) || '';
    }

    goToCreationContract() {
        this.openContract = true;
        this.contract = new Contract({});
    }

    goToEditionContract(contract: Contract) {
        this.openContract = true;
        this.contract = _.cloneDeep(contract);
        this.contract.isEditing = true;
    }

    goToRemoveContract(replacementContractToRemove: Contract) {
        this.contract = replacementContractToRemove;
        this.showDeleteModal = true;
    }

    cancel() {
        this.openContract = false;
        this.contract = null;
        this.submitted = false;
        this.fistContractRegister = false;
    }

    removeContract() {
        this.showDeleteModal = false;
        this.loading = true;
        if (this.contract) {
            ReplacementContractsService.removeReplacementContract(this.$props.replacementListCandidateId, this.contract)
                .then(() => {
                    this.initList();
                    this.loading = false;
                    this.openContract = false;
                    this.fistContractRegister = false;
                    ToasterService.showSuccess(i18n.t('lang.toaster.saveChanged') as string, i18n.t('lang.toaster.goodJob') as string);
                    this.$emit('updateReplacementListIsModify');
                })
                .catch(() => {
                    this.loading = false;
                    ToasterService.showError(i18n.t('lang.toaster.errorGeneralTitle') as string);
                });
        } else {
            this.loading = false;
            ToasterService.showError(i18n.t('lang.toaster.errorGeneralTitle') as string);
        }
    }

    saveContract() {
        this.submitted = true;
        if (this.$refs.replacementContractForm.querySelectorAll(':invalid').length || this.$refs.replacementContractForm.querySelectorAll('.error').length) {
            return;
        }
        if (this.contract) {
            this.loading = true;
            const serviceToRequest = this.contract.id ? ReplacementContractsService.updateReplacementContract : ReplacementContractsService.createReplacementContract;
            serviceToRequest(this.$props.replacementListCandidateId, this.contract).then(() => {
                this.openContract = false;
                this.loading = false;
                this.submitted = false;
                this.fistContractRegister = false;
                ToasterService.showSuccess(i18n.t('lang.toaster.saveChanged') as string);
                this.initList();
                this.$emit('updateReplacementListIsModify');
            }).catch(error => {
                this.loading = false;
                this.openContract = false;
                console.log(error);
            });
        }
    }

    getReplacementContracts() {
        ReplacementContractsService.getReplacementContracts(this.$props.replacementListCandidateId).then(response => {
            this.replacementContracts = response.replacementContracts.map(contract => new Contract(contract));
            this.replacementContracts.forEach((contract, index) => contract.order = this.replacementContracts.length - index);
            this.loading = false;
        }).catch(error => console.log(error));  
    }

    initList() {
        this.loading = true;
        this.getReplacementContracts();
    }

    mounted() {
        this.initList();
    }
}
